// src/screens/ResponderCotizacionScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
// eslint-disable-next-line no-unused-vars
import { IoArrowBack, IoDownloadOutline, IoMapOutline, IoTimeOutline, IoCalendarOutline, IoCashOutline, IoSend, IoPersonCircleOutline, IoAlertCircle } from 'react-icons/io5';
import moment from 'moment';
import { getProviderBusySlots, getProviderSchedule, isDayWorking, getWorkingSlots } from '../utils/availabilityService';
import './ResponderCotizacionScreen.css';

const getTodayString = () => {
  return new Date().toISOString().split('T')[0];
}

// Initial default, will be replaced by dynamic slots
const defaultTimeSlots = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00'
];

function ResponderCotizacionScreen() {
  const navigate = useNavigate();
  const { cotizacionId } = useParams();
  // eslint-disable-next-line no-unused-vars
  const { currentUser, userData } = useAuth();

  const [cotizacion, setCotizacion] = useState(null);
  const [condominioDireccion, setCondominioDireccion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [costoMin, setCostoMin] = useState('');
  const [costoMax, setCostoMax] = useState('');
  const [comentarios, setComentarios] = useState('');
  const [tiempoCantidad, setTiempoCantidad] = useState('2');
  const [tiempoUnidad, setTiempoUnidad] = useState('hours'); // 'hours', 'days', 'minutes'
  // eslint-disable-next-line no-unused-vars
  const [validezDias, setValidezDias] = useState(3);

  // Availability Logic
  const [diaServicio, setDiaServicio] = useState(getTodayString());
  const [horaServicio, setHoraServicio] = useState('');
  const [busySlots, setBusySlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [providerSchedule, setProviderSchedule] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState(defaultTimeSlots);

  useEffect(() => {
    // Verificar permisos
    if (!userData || !userData.proveedorId) {
      if (userData && !userData.proveedorId) {
        setLoading(false);
        setError("No tienes un perfil de proveedor asociado.");
      }
      return;
    }

    if (!cotizacionId) return;

    const fetchDatos = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "cotizaciones", cotizacionId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.proveedorId !== userData.proveedorId) {
            setError("No tienes permiso para ver esta cotización.");
            setLoading(false);
            return;
          }
          setCotizacion(data);

          if (data.condominioId) {
            const condoRef = doc(db, "condominios", data.condominioId);
            const condoSnap = await getDoc(condoRef);
            if (condoSnap.exists() && condoSnap.data().direccion) {
              setCondominioDireccion(condoSnap.data().direccion);
            }
          }
        } else {
          setError("No se encontró la cotización.");
        }
      } catch (e) {
        setError("Error al cargar la cotización.");
        console.error(e);
      }
      setLoading(false);
    };

    fetchDatos();
  }, [cotizacionId, userData]);

  // Fetch Busy Slots
  useEffect(() => {
    const fetchBusyAndSchedule = async () => {
      if (!userData?.proveedorId) return;

      // 1. Fetch Schedule Config
      const schedule = await getProviderSchedule(userData.proveedorId);
      setProviderSchedule(schedule);

      if (!diaServicio) return;

      // 2. Fetch Busy Slots
      setLoadingSlots(true);

      // Fix: Ensure we create a date in LOCAL time, not UTC
      const [y, m, d] = diaServicio.split('-').map(Number);
      const start = new Date(y, m - 1, d, 0, 0, 0, 0);
      const end = new Date(y, m - 1, d, 23, 59, 59, 999);

      const slots = await getProviderBusySlots(userData.proveedorId, start, end, cotizacionId);
      setBusySlots(slots);
      setLoadingSlots(false);
    };
    fetchBusyAndSchedule();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diaServicio, userData]);

  // Update available slots when day or schedule changes
  useEffect(() => {
    if (diaServicio) {
      if (providerSchedule) {
        if (!isDayWorking(diaServicio, providerSchedule)) {
          // Could alert or disable
          setAvailableTimeSlots([]);
        } else {
          const slots = getWorkingSlots(diaServicio, providerSchedule);
          setAvailableTimeSlots(slots);
        }
      } else {
        setAvailableTimeSlots(defaultTimeSlots);
      }
    }
  }, [diaServicio, providerSchedule]);

  const isTimeBlocked = (timeStr) => {
    if (!diaServicio) return true;
    const slotStart = moment(`${diaServicio}T${timeStr}`);

    // 1. Past Check (give 30 min buffer)
    if (slotStart.isBefore(moment().add(30, 'minutes'))) return true;

    // Dynamic blocking based on user input
    let durationH = 2; // default
    if (tiempoCantidad) {
      const val = parseFloat(tiempoCantidad);
      if (tiempoUnidad === 'minutes') durationH = val / 60;
      else if (tiempoUnidad === 'hours') durationH = val;
      else if (tiempoUnidad === 'days') durationH = val * 24;
    }
    const slotEnd = moment(slotStart).add(durationH, 'hours');

    return busySlots.some(busy => {
      const busyStart = moment(busy.start);
      const busyEnd = moment(busy.end);
      return (slotStart < busyEnd && slotEnd > busyStart);
    });
  };

  const handleSubmitEstimado = async (e) => {
    e.preventDefault();

    if (Number(costoMin) <= 0 || Number(costoMax) <= 0 || !tiempoCantidad || !diaServicio || !horaServicio) {
      alert("Por favor completa todos los campos (Costos, Tiempo, Día y Hora).");
      return;
    }
    if (Number(costoMax) < Number(costoMin)) {
      alert("El Estimado Máximo no puede ser menor al Mínimo.");
      return;
    }

    const [hora, minutos] = horaServicio.split(':');
    const [y, m, d] = diaServicio.split('-').map(Number);
    const fechaHoraSugerida = new Date(y, m - 1, d, hora, minutos, 0, 0);

    if (fechaHoraSugerida < new Date()) {
      alert("La fecha y hora del servicio no pueden ser en el pasado.");
      return;
    }

    setEnviando(true);
    try {
      const docRef = doc(db, "cotizaciones", cotizacionId);
      const fechaValidez = Timestamp.fromDate(new Date(Date.now() + validezDias * 24 * 60 * 60 * 1000));

      // Construct human readable string for persistence
      let tiempoStr = "";
      if (tiempoUnidad === 'hours') tiempoStr = `${tiempoCantidad} horas`;
      else if (tiempoUnidad === 'days') tiempoStr = `${tiempoCantidad} días`;
      else if (tiempoUnidad === 'minutes') tiempoStr = `${tiempoCantidad} minutos`;

      await updateDoc(docRef, {
        costoMin: Number(costoMin),
        costoMax: Number(costoMax),
        comentariosProveedor: comentarios,
        tiempoServicio: tiempoStr,
        validezCotizacion: fechaValidez,
        fechaHoraServicioSugerido: Timestamp.fromDate(fechaHoraSugerida),
        estatus: "pendiente_residente"
      });

      // Feedback amigable
      const confirm = window.confirm("¡Propuesta enviada con éxito! ¿Quieres volver a la lista?");
      if (confirm) navigate('/proveedor/cotizaciones');
      else setEnviando(false); // Stay to verify? Usually navigate is better.
      // Actually force navigate
      navigate('/proveedor/cotizaciones');

    } catch (e) {
      console.error("Error al enviar estimado: ", e);
      alert("Hubo un error al guardar tu respuesta. Intenta de nuevo.");
      setEnviando(false);
    }
  };

  if (loading) return <div className="response-container"><div className="loader"></div></div>;
  if (error) return (
    <div className="response-container">
      {/* Sticky Compact Header (Error Case) */}
      <div className="app-header-sticky">
        <div className="kondo-banner-standard">
          <div className="banner-standard-content">
            <button onClick={() => navigate('/proveedor/cotizaciones')} className="banner-back-btn-standard">
              <IoArrowBack size={24} />
            </button>
            <div className="banner-standard-text">
              <h2>Error</h2>
              <p>Ocurrió un problema</p>
            </div>
          </div>
          <IoAlertCircle className="banner-standard-icon-decor" />
        </div>
      </div>
      <div className="empty-state-card" style={{ padding: 30, textAlign: 'center', marginTop: '20px' }}>
        <IoAlertCircle size={50} color="#ef4444" />
        <p style={{ color: '#ef4444' }}>{error}</p>
      </div>
    </div>
  );
  if (!cotizacion) return null;

  return (
    <div className="response-container">
      {/* Sticky Compact Header */}
      <div className="app-header-sticky">
        <div className="kondo-banner-standard">
          <div className="banner-standard-content">
            <button onClick={() => navigate('/proveedor/cotizaciones')} className="banner-back-btn-standard">
              <IoArrowBack size={24} />
            </button>
            <div className="banner-standard-text">
              <h2>Responder Solicitud</h2>
              <p>Envía tu propuesta comercial</p>
            </div>
          </div>
          <IoSend className="banner-standard-icon-decor" />
        </div>
      </div>

      <div className="response-grid">
        {/* COL 1: DETALLE SOLICITUD */}
        <div className="response-card">
          <h3>Detalles del Servicio</h3>

          <div className="info-row">
            <span className="info-label">Residente</span>
            <div className="info-value">
              <IoPersonCircleOutline size={20} color="#6b7280" />
              {cotizacion.nombreResidente} ({cotizacion.unidadResidente})
            </div>
          </div>

          <div className="info-row">
            <span className="info-label">Categoría / Servicio</span>
            <div className="info-value">
              {cotizacion.categoria || "Servicio General"} - {cotizacion.tipoServicio}
            </div>
          </div>

          {condominioDireccion && (
            <div className="info-row">
              <span className="info-label">Ubicación</span>
              <div className="info-value">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(condominioDireccion)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="maps-link"
                >
                  <IoMapOutline /> {condominioDireccion}
                </a>
              </div>
            </div>
          )}

          <div className="info-row">
            <span className="info-label">Problema Reportado</span>
            <p style={{ margin: '5px 0', lineHeight: '1.5', color: '#374151', background: '#f9fafb', padding: 10, borderRadius: 8 }}>
              "{cotizacion.descripcion}"
            </p>
          </div>

          {cotizacion.adjuntosUrls && cotizacion.adjuntosUrls.length > 0 && (
            <div className="info-row">
              <span className="info-label">Evidencia Adjunta ({cotizacion.adjuntosUrls.length})</span>
              <div className="attachments-grid">
                {cotizacion.adjuntosUrls.map((url, index) => (
                  <a key={index} href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    {url.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                      <img src={url} alt="adjunto" className="attachment-thumbnail" />
                    ) : (
                      <div className="attachment-file">
                        <IoDownloadOutline size={20} />
                        <span>Archivo</span>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* COL 2: FORMULARIO RESPUESTA */}
        <div className="response-card">
          <h3>Tu Propuesta</h3>
          <form onSubmit={handleSubmitEstimado}>
            <div style={{ display: 'flex', gap: 15 }}>
              <div className="form-group-modern" style={{ flex: 1 }}>
                <label>Estimado Mínimo ($)</label>
                <div style={{ position: 'relative' }}>
                  <IoCashOutline style={{ position: 'absolute', left: 10, top: 12, color: '#9ca3af' }} />
                  <input
                    type="number"
                    value={costoMin}
                    onChange={e => setCostoMin(e.target.value)}
                    className="form-input-modern"
                    style={{ paddingLeft: 35 }}
                    placeholder="0.00"
                    min="0"
                  />
                </div>
              </div>
              <div className="form-group-modern" style={{ flex: 1 }}>
                <label>Estimado Máximo ($)</label>
                <div style={{ position: 'relative' }}>
                  <IoCashOutline style={{ position: 'absolute', left: 10, top: 12, color: '#9ca3af' }} />
                  <input
                    type="number"
                    value={costoMax}
                    onChange={e => setCostoMax(e.target.value)}
                    className="form-input-modern"
                    style={{ paddingLeft: 35 }}
                    placeholder="0.00"
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="form-group-modern">
              <label>Tiempo Estimado</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <IoTimeOutline style={{ position: 'absolute', left: 10, top: 12, color: '#9ca3af' }} />
                  <input
                    type="number"
                    value={tiempoCantidad}
                    onChange={e => { setTiempoCantidad(e.target.value); setHoraServicio(''); }}
                    className="form-input-modern"
                    style={{ paddingLeft: 35 }}
                    placeholder="Cantidad"
                    min="1"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <select
                    value={tiempoUnidad}
                    onChange={e => { setTiempoUnidad(e.target.value); setHoraServicio(''); }}
                    className="form-input-modern"
                    style={{ appearance: 'auto' }} // Ensure arrow shows
                  >
                    <option value="minutes">Minutos</option>
                    <option value="hours">Horas</option>
                    <option value="days">Días</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              <div className="form-group-modern" style={{ flex: 1 }}>
                <label>Fecha Propuesta</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="date"
                    value={diaServicio}
                    onChange={e => { setDiaServicio(e.target.value); setHoraServicio(''); }}
                    className="form-input-modern"
                    min={getTodayString()}
                  />
                </div>
              </div>

              <div className="form-group-modern" style={{ flex: 1 }}>
                <label>Horario Inicio (Disponibilidad)</label>
                {loadingSlots ? <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>Verificando agenda...</p> : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', padding: 8, borderRadius: 8 }}>
                    {availableTimeSlots.length === 0 ? (
                      <div style={{ gridSize: 'span 4', colSpan: 4, textAlign: 'center', padding: 10, color: '#ef4444', fontSize: '0.85rem' }}>
                        No laboras este día o no hay horarios disponibles.
                      </div>
                    ) : availableTimeSlots.map(time => {
                      const blocked = isTimeBlocked(time);
                      return (
                        <button
                          key={time}
                          type="button"
                          disabled={blocked}
                          onClick={() => setHoraServicio(time)}
                          style={{
                            padding: '6px 2px',
                            fontSize: '0.8rem',
                            borderRadius: 4,
                            border: (horaServicio === time) ? 'none' : '1px solid #d1d5db',
                            background: (horaServicio === time) ? '#10b981' : (blocked ? '#f3f4f6' : 'white'),
                            color: (horaServicio === time) ? 'white' : (blocked ? '#d1d5db' : '#374151'),
                            cursor: blocked ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                )}
                {horaServicio && <p style={{ fontSize: '0.85rem', color: '#10b981', marginTop: 5 }}>Seleccionado: {moment(horaServicio, 'HH:mm').format('h:mm A')}</p>}
              </div>
            </div>

            <div className="form-group-modern">
              <label>Comentarios Adicionales</label>
              <textarea
                rows={3}
                value={comentarios}
                onChange={e => setComentarios(e.target.value)}
                className="form-textarea-modern"
                placeholder="Describe qué incluye tu servicio, materiales, etc."
              />
            </div>

            <button type="submit" className="btn-submit-response" disabled={enviando}>
              {enviando ? 'Enviando...' : <><IoSend /> Enviar Propuesta</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResponderCotizacionScreen;